<script setup lang="ts">
  import FieldOrderer from '@/components/form-wizard/FieldOrderer.vue'
  import { wizardConfig } from '@/configs/wizard-element-config'
  import { useFlowConfigStore } from '@/store/flow-config.store.ts'
  import { debounce } from '@/utils/debounce'
  import {
    ConfirmationDialog,
    require,
    UiButton,
    useToastStore,
    type FlowField,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, ref, watch } from 'vue'

  defineOptions({ inheritAttrs: false })

  const props = defineProps<{
    currentPage: number
  }>()

  const toastStore = useToastStore()
  const store = useFlowConfigStore()
  const selectedFieldId = defineModel<string | undefined>('selectedFieldId')
  const currentFields = computed(() => store.config?.pages[props.currentPage]?.fields ?? [])
  const field = computed(() =>
    currentFields.value.find(field => field?.id === selectedFieldId.value)
  )

  const elementConfig = computed(() =>
    wizardConfig.find(
      ({ type: wizardType }) =>
        wizardType === (field.value?.type === 'custom' ? field.value.customType : field.value?.type)
    )
  )

  const show = ref(false)

  function removeSelectedField(id: string): void {
    const page = store.config?.pages[props.currentPage]
    if (!page) return
    page.fields = page.fields.filter((field: FlowField) => field?.id !== id)

    store.deleteField(id, page.id)

    toastStore.success(`Field deleted successfully.`)

    selectedFieldId.value = undefined
  }

  function updateField(field: FlowField) {
    //TODO Validate the config based on the rendered component
    store.updateField(field)

    toastStore.success(`Field updated successfully.`)
  }

  const debouncedUpdateField = debounce(async () => {
    if (!field.value) return
    updateField(field.value)
  }, 500)

  watch(
    field,
    (newField, oldField) => {
      if (!newField || newField.id !== oldField?.id) return
      debouncedUpdateField()
    },
    { deep: true }
  )
</script>

<template>
  <div class="renderer">
    <FieldOrderer
      v-if="selectedFieldId"
      :currentPage="props.currentPage"
      :fieldId="require(selectedFieldId)"
    />
    <div class="renderer-scroll">
      <component
        v-if="field"
        :is="require(elementConfig, 'Component does not exist').fieldConfig"
        v-model="field"
        :key="field.id"
      />
    </div>

    <div class="actions">
      <UiButton class="button" icon="delete" variant="negative" @click="show = true" />
    </div>
  </div>

  <ConfirmationDialog
    title="Are you sure you want to remove this item?"
    body="This action cannot be undone."
    confirmButtonText="Remove"
    confirmButtonColor="negative"
    @update:show="show = $event"
    :show="show"
    :confirm="() => field?.id && removeSelectedField(field.id)"
  />
</template>

<style scoped>
  .renderer {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .renderer-scroll {
    flex: 1;
    padding: var(--spacing-xs);
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
  }

  .actions {
    padding: var(--spacing-xs) 0;
    display: flex;
    gap: var(--spacing-xs);
    margin-top: 0.75rem;
    flex-shrink: 0;
  }

  .button {
    justify-content: center;
  }
</style>
